import * as React from 'react';
import { Fab as MatFab, FabProps as MatFabProps, SxProps, Theme, Zoom } from '@mui/material';
import { archerConstants, theme } from '../../utils';
import { IconKey, Icon } from '../Icon';
import { Typography } from '../Typography';

export type FabProps = {
  classes?: MatFabProps['classes'];
  className?: string;
  color?: MatFabProps['color'];
  disabled?: boolean;
  disableFocusRipple?: boolean;
  disableRipple?: boolean;
  href?: string;
  onClick?: (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void;
  size?: MatFabProps['size'];
  variant?: MatFabProps['variant'];
  children?: any;
  title: string;
  icon: IconKey;
  type: 'primary' | 'secondary' | 'destructive';
  sx?: SxProps<Theme>;
};

const fabSize = theme.spacing(5.5);

const styles: Record<string, SxProps<Theme>> = {
  fabCommon: {
    padding: 0,
    width: fabSize,
    minWidth: fabSize,
    height: fabSize,
    minHeight: fabSize,
    transition: 'all 0.2s ease',
    '&:hover, &:active, &:focus': {
      width: 'auto',
      height: 'auto',
      '& [class*="fabLabelText"]': {
        width: 'auto',
        opacity: 1,
        paddingLeft: 1.5,
        paddingRight: 1,
        paddingY: 0.5
      },
      '& [class*="fabIcon"]': {
        marginRight: 1.5
      }
    }
  },
  fabText: {
    width: 0,
    opacity: 0,
    overflow: 'hidden',
    lineHeight: '1.25',
    fontSize: '1rem',
    fontWeight: 700
  },
  fabPrimary: {
    backgroundColor: 'primary.main',
    color: 'background.paper',
    '&:hover': {
      backgroundColor: archerConstants.archerPrimaryButtonHover
    },
    '&:focus, &:active': {
      backgroundColor: archerConstants.archerPrimaryButtonActive
    },
    '&.Mui-disabled': {
      color: archerConstants.archerBackgroundPaper
    }
  },
  fabSecondary: {
    backgroundColor: 'background.paper',
    color: 'primary.main',
    border: '1px solid',
    borderColor: 'primary.main',
    '&:hover': {
      color: archerConstants.archerContrastRatioSafe,
      backgroundColor: archerConstants.archerButtonHover,
      border: `1px solid ${archerConstants.archerContrastRatioSafe}`
    },
    '&:focus, &:active': {
      color: archerConstants.archerContrastRatioSafe,
      backgroundColor: archerConstants.archerButtonActive,
      border: `1px solid ${archerConstants.archerContrastRatioSafe}`
    }
  },
  fabDestructive: {
    backgroundColor: 'background.paper',
    color: 'primary.main',
    border: 'none',
    '&:hover': {
      color: archerConstants.archerContrastRatioSafe,
      backgroundColor: archerConstants.archerButtonHover
    },
    '&:focus, &:active': {
      color: archerConstants.archerContrastRatioSafe,
      backgroundColor: archerConstants.archerButtonActive
    }
  }
};

const Fab: React.FC<FabProps> = ({
  classes,
  className,
  color,
  disabled,
  disableFocusRipple,
  disableRipple = true,
  href,
  onClick,
  size,
  variant = 'extended',
  title,
  icon,
  type,
  sx,
  ...rest
}) => {
  const { fabCommon, fabPrimary, fabSecondary, fabDestructive, fabText } = styles;

  const getTypeStyle = () => {
    let style = undefined;
    switch (type) {
      case 'primary':
        style = fabPrimary;
        break;
      case 'secondary':
        style = fabSecondary;
        break;
      case 'destructive':
        style = fabDestructive;
        break;
    }
    return style;
  };

  return (
    <Zoom in={true}>
      <MatFab
        sx={[fabCommon, getTypeStyle(), sx] as SxProps<Theme>}
        classes={classes}
        title={title}
        className={className}
        color={color}
        disabled={disabled}
        disableFocusRipple={disableFocusRipple}
        disableRipple={disableRipple}
        href={href}
        size={size}
        variant={variant}
        onClick={onClick}
        role="button"
        {...rest}
      >
        <Typography variant="body1" sx={[fabText, sx] as SxProps<Theme>} className="fabLabelText">
          {title}
        </Typography>
        <Icon icon={icon} fontSize="medium" className="fabIcon" />
      </MatFab>
    </Zoom>
  );
};

Fab.displayName = 'Fab';

export { Fab };
